import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RuntimeEnvironmentService } from './runtime-environment.service';

export interface MiscMessage {
  miscMessageId: number;
  title: string;
  content: string;
  priority: number;
  createdOn: string;
  isActive: boolean;
  expiresOn?: string | null;
  modifiedOn?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MiscMessageService {
  private readonly apiUrl: string;
  private readonly messageSeenDateKey = 'messageSeenDate';

  constructor(
    private http: HttpClient,
    private runtimeEnv: RuntimeEnvironmentService
  ) {
    this.apiUrl = `${this.runtimeEnv.apiConfig.uri}/miscmessages`;
  }

  getActiveMessages(): Observable<MiscMessage[]> {
    const lastSeenDate = this.getLastSeenDate();
    const params = lastSeenDate ? `?since=${encodeURIComponent(lastSeenDate)}` : '';
    return this.http.get<MiscMessage[]>(`${this.apiUrl}/active${params}`);
  }

  markAllAsRead(): void {
    const now = new Date().toISOString();
    localStorage.setItem(this.messageSeenDateKey, now);
  }

  resetSeenMessages(): void {
    localStorage.removeItem(this.messageSeenDateKey);
  }

  getLastSeenDate(): string | null {
    return localStorage.getItem(this.messageSeenDateKey);
  }

  getUnreadMessages(): Observable<MiscMessage[]> {
    return this.getActiveMessages();
  }

  getAllMessages(): Observable<MiscMessage[]> {
    return this.http.get<MiscMessage[]>(this.apiUrl);
  }

  getMessage(id: number): Observable<MiscMessage> {
    return this.http.get<MiscMessage>(`${this.apiUrl}/${id}`);
  }

  createMessage(message: Partial<MiscMessage>): Observable<number> {
    return this.http.post<number>(this.apiUrl, message);
  }

  updateMessage(id: number, message: Partial<MiscMessage>): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}`, { ...message, id });
  }

  deleteMessage(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}